import styles from './MaterialsPagination.module.css';

interface Props {
  currentPage: number;        // 1始まり
  totalPages: number;
  onPageChange: (page: number) => void;
  /** ページ取得中などで操作を抑止したいとき */
  disabled?: boolean;
}

/**
 * 表示するページ番号の並びを作る。先頭/末尾/現在ページ周辺を出し、間は省略記号 (…)。
 * 例: 1 … 7 [8] 9 … 17
 */
function buildPageItems(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items: Array<number | 'ellipsis'> = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  items.push(1);
  if (left > 2) items.push('ellipsis');
  for (let p = left; p <= right; p++) items.push(p);
  if (right < total - 1) items.push('ellipsis');
  items.push(total);
  return items;
}

/**
 * 素材ブラウズページ用のページ番号ジャンプ。
 * `< 1 … 8 9 10 … 17 >` の形で任意ページへ移動する。
 */
export function MaterialsPagination({ currentPage, totalPages, onPageChange, disabled = false }: Props) {
  if (totalPages <= 1) return null;

  const items = buildPageItems(currentPage, totalPages);
  const go = (p: number) => {
    if (disabled) return;
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== currentPage) onPageChange(next);
  };

  return (
    <nav className={styles.pagination} aria-label="ページネーション">
      <button
        type="button"
        className={styles.navButton}
        onClick={() => go(currentPage - 1)}
        disabled={disabled || currentPage <= 1}
        aria-label="前のページ"
      >
        ‹
      </button>

      {items.map((item, idx) =>
        item === 'ellipsis' ? (
          <span key={`e${idx}`} className={styles.ellipsis} aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            className={`${styles.pageButton}${item === currentPage ? ' ' + styles.active : ''}`}
            onClick={() => go(item)}
            disabled={disabled}
            aria-current={item === currentPage ? 'page' : undefined}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        className={styles.navButton}
        onClick={() => go(currentPage + 1)}
        disabled={disabled || currentPage >= totalPages}
        aria-label="次のページ"
      >
        ›
      </button>
    </nav>
  );
}
